import { Response } from './api';
import { merge } from 'lodash';
import { normalizeCart } from '~/utilities/cartNormalize';
import { getCartToken } from '~/utilities/cart';

const JWT_IDENTITY_DEDUPE_WINDOW_MS = 2000;
let jwtIdentityInFlight = null;
let jwtIdentityLast = { key: null, at: 0, response: null };

const persistentCart = {
    create: async (
        items = [],
        totals = null,
        customer = null,
        idempotencyKey = null
    ) => {
        const payload = {
            items: items,
            totals: totals,
            customer: customer
        };

        const headers = {
            'Content-Type': 'application/json'
        };

        if (idempotencyKey) {
            headers['Idempotency-Key'] = idempotencyKey;
        }

        return await cartFetch({
            options: {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(payload)
            }
        });
    },
    get: async (cartID, email = null) => {
        return await cartFetch({
            cartID: cartID,
            email: email,
            options: { method: 'GET' }
        });
    },
    recover: async (token, message_token, touch) => {
        if (!token || typeof token !== 'string' || token.trim() === '') {
            throw new Error('Invalid recovery token');
        }

        try {
            const params = new URLSearchParams();
            params.append('token', token);
            params.append('message_token', message_token);
            params.append('touch', touch);

            const url = `${process.env.GATSBY_CMS_BASE_URL}/wp-json/autobell/v1/api/cart/recover?${params.toString()}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Referer':
                        typeof window !== 'undefined'
                            ? window.location.origin
                            : ''
                }
            });

            if (!response.ok) {
                let errorData = null;
                try {
                    errorData = await response.json();
                } catch (e) {}

                const error = new Error(
                    `HTTP ${response.status}: ${response.statusText}`
                );
                error.status = response.status;
                error.data = errorData || {
                    status: 'error',
                    error: {
                        type: 'network_error',
                        message: `HTTP ${response.status}: ${response.statusText}`
                    }
                };
                throw error;
            }

            const data = await response.json();
            return data;
        } catch (error) {
            if (error.status !== undefined) {
                throw error;
            }
            const networkError = new Error(
                error.message || 'Network request failed'
            );
            networkError.status = 0;
            networkError.data = {
                status: 'error',
                error: {
                    type: 'network_error',
                    message: error.message || 'Network request failed'
                }
            };
            throw networkError;
        }
    },
    update: async (payload, headers = {}) => {
        return await cartFetch({
            options: {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...headers },
                body: JSON.stringify(payload)
            }
        });
    },
    identify: async (cart_id, email, force_replace = false) => {
        const payload = {
            cart_id,
            email,
            force_replace
        };

        return await cartFetch({
            endpoint: 'identity',
            options: {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            }
        });
    },
    identifyWithJwtToken: async (email, jwtToken) => {
        const key = `${(email || '').toLowerCase()}|${jwtToken || ''}`;

        if (jwtIdentityInFlight && jwtIdentityInFlight.key === key) {
            return jwtIdentityInFlight.promise;
        }

        if (
            jwtIdentityLast.key === key &&
            jwtIdentityLast.response !== null &&
            Date.now() - jwtIdentityLast.at < JWT_IDENTITY_DEDUPE_WINDOW_MS
        ) {
            return jwtIdentityLast.response;
        }

        const payload = {
            email
        };

        const promise = cartFetch({
            endpoint: 'identity',
            options: {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${jwtToken}`
                },
                body: JSON.stringify(payload)
            },
            returnJson: true
        })
            .then((response) => {
                jwtIdentityLast = { key, at: Date.now(), response };
                return response;
            })
            .finally(() => {
                if (jwtIdentityInFlight && jwtIdentityInFlight.key === key) {
                    jwtIdentityInFlight = null;
                }
            });

        jwtIdentityInFlight = { key, promise };
        return promise;
    },
    completeCheckout: async (
        cartId,
        orderId,
        totals,
        metadata,
        succeededItems = null,
        complete = true
    ) => {
        if (!cartId || typeof cartId !== 'string' || cartId.trim() === '') {
            throw new Error('Invalid cartId');
        }

        if (orderId !== undefined && orderId !== null) {
            if (typeof orderId === 'number') {
                orderId = String(orderId);
            }
            if (typeof orderId !== 'string' || orderId.trim() === '') {
                throw new Error('Invalid orderId');
            }
        }

        const payload = {
            cart_id: cartId,
            complete: complete
        };

        if (orderId) {
            payload.order_id = orderId;
        }

        if (totals) {
            const final_totals = {};
            const toCents = (value) =>
                Math.max(0, Math.round((Number(value) || 0) * 100));

            if (totals.subTotal !== undefined) {
                final_totals.subtotal_cents = toCents(totals.subTotal);
            }

            if (totals.tax !== undefined) {
                final_totals.tax_cents = toCents(totals.tax);
            }

            if (totals.shipping !== undefined) {
                final_totals.shipping_cents = toCents(totals.shipping);
            }

            if (totals.discount !== undefined) {
                final_totals.discount_cents = toCents(totals.discount);
            }

            if (totals.grandTotal !== undefined) {
                final_totals.total_cents = toCents(totals.grandTotal);
            }

            if (Object.keys(final_totals).length > 0) {
                payload.final_totals = final_totals;
            }
        }

        if (metadata) {
            const cleanedMetadata = {};
            for (const key in metadata) {
                if (metadata[key] !== undefined) {
                    cleanedMetadata[key] = metadata[key];
                }
            }
            if (Object.keys(cleanedMetadata).length > 0) {
                payload.metadata = cleanedMetadata;
            }
        }

        if (
            succeededItems &&
            Array.isArray(succeededItems) &&
            succeededItems.length > 0
        ) {
            payload.succeeded_items = succeededItems;
        }

        try {
            const response = await fetch(
                `${process.env.GATSBY_CMS_BASE_URL}/wp-json/autobell/v1/api/checkout/complete`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Referer':
                            typeof window !== 'undefined'
                                ? window.location.origin
                                : ''
                    },
                    body: JSON.stringify(payload)
                }
            );

            if (!response.ok) {
                let errorMessage = 'Checkout completion failed';
                try {
                    const json = await response.json();
                    if (json.message) {
                        errorMessage = json.message;
                    }
                } catch (e) {}
                throw new Error(errorMessage);
            }

            return await response.json();
        } catch (error) {
            if (error.message && error.message !== 'Failed to fetch') {
                throw error;
            }
            throw new Error('Network error completing checkout');
        }
    }
};

export default persistentCart;

/**
 * @param {String} cartID
 * @param {String} email - Optional email to search for cart by email
 * @param {String} token - Optional token for cart recovery
 * @param {String} endpoint - Optional endpoint path (e.g., 'identity')
 * @param {Object} options {@link https://developer.mozilla.org/en-US/docs/Web/API/fetch#options}
 * @param {Boolean} returnJson
 * @returns {Promise<any>}
 */
export const cartFetch = async ({
    cartID,
    email,
    token,
    endpoint,
    options,
    returnJson = false
}) => {
    let url = `${process.env.GATSBY_CMS_BASE_URL}/wp-json/autobell/v1/api/cart`;

    if (endpoint) {
        url += `/${endpoint}`;
    }

    if (options && options.method === 'GET') {
        const params = new URLSearchParams();
        if (cartID) {
            params.append('cart_id', cartID);
        }
        if (email) {
            params.append('email', email);
        }
        if (token) {
            params.append('token', token);
        }
        if (params.toString()) {
            url += `?${params.toString()}`;
        }
    }

    const fetchOptions = {
        headers: {
            'Content-Type': 'application/json'
        }
    };

    merge(fetchOptions, options);

    const method = fetchOptions.method?.toUpperCase();
    const isWriteEndpoint =
        (method === 'PUT' && url.includes('/cart')) ||
        (method === 'DELETE' && url.includes('/cart')) ||
        (method === 'POST' &&
            (url.includes('/identity') || url.includes('/heartbeat')));

    if (isWriteEndpoint) {
        const token = getCartToken();
        if (token) {
            const parsed =
                typeof fetchOptions.body === 'string'
                    ? JSON.parse(fetchOptions.body)
                    : fetchOptions.body || {};
            parsed.cart_token = token;
            fetchOptions.body = JSON.stringify(parsed);

            if (process.env.NODE_ENV !== 'production') {
                console.debug('[CartAuth] Added cart_token:', token);
            }
        }
    }
    try {
        const response = await fetch(url, fetchOptions);

        try {
            const json = await response.json();

            // If response is not ok, pass through the error structure
            if (!response.ok) {
                return new Response(json, false);
            }

            const normalized = normalizeCart(json);

            if (returnJson && endpoint === 'identity' && json.cart) {
                return {
                    authorized: json.authorized,
                    status: json.status,
                    cart: normalizeCart(json.cart)
                };
            }

            const result = returnJson
                ? normalized
                : new Response(normalized, response.ok);
            return result;
        } catch (error) {
            if (!response.ok) {
                return new Response(
                    {
                        error: {
                            id: response.status,
                            message: response.statusText
                        }
                    },
                    false
                );
            } else {
                return new Response(
                    {
                        success: true
                    },
                    response.ok
                );
            }
        }
    } catch (error) {
        console.error(error);

        return new Response({
            error: {
                id: 'UNKNOWN',
                message: error.message
            }
        });
    }
};
